import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  Card,
  Switch,
} from '@mui/joy';
import { LockIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from 'renderer/lib/authContext';
import { useNotification } from 'renderer/components/Shared/NotificationSystem';
import * as chatAPI from 'renderer/lib/transformerlab-api-sdk';

type PrivacyMode = 'team' | 'private';

interface ExperimentPrivacyResponse {
  mode: PrivacyMode;
}

export default function ExperimentPrivacySection({
  teamId,
}: {
  teamId: string;
}) {
  const { fetchWithAuth } = useAuth();
  const { addNotification } = useNotification();
  const [mode, setMode] = useState<PrivacyMode>('team');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Overlapping runs (teamId changes, or a new fetchWithAuth identity) can resolve
    // out of order. This gates every post-await setter so only the latest run wins --
    // a stale write here would show the wrong visibility state on a security toggle.
    let cancelled = false;

    const fetchMode = async () => {
      if (!teamId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await fetchWithAuth(
          chatAPI.Endpoints.Teams.GetExperimentPrivacy(teamId),
        );
        if (res.ok) {
          const data: ExperimentPrivacyResponse = await res.json();
          if (cancelled) return;
          setMode(data.mode === 'private' ? 'private' : 'team');
          setError(null);
        } else {
          if (cancelled) return;
          setError('Could not load the experiment visibility setting.');
        }
      } catch (e) {
        if (cancelled) return;
        console.error('Error fetching experiment privacy mode:', e);
        setError('Could not load the experiment visibility setting.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMode();

    return () => {
      cancelled = true;
    };
  }, [teamId, fetchWithAuth]);

  const handleToggle = async (checked: boolean) => {
    const nextMode: PrivacyMode = checked ? 'private' : 'team';
    const previousMode = mode;

    setSaving(true);
    setMode(nextMode);
    try {
      const res = await fetchWithAuth(
        chatAPI.Endpoints.Teams.SetExperimentPrivacy(teamId),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode: nextMode }),
        },
      );

      if (res.ok) {
        const data: ExperimentPrivacyResponse = await res.json();
        setMode(data.mode === 'private' ? 'private' : 'team');
        addNotification({
          type: 'success',
          message:
            data.mode === 'private'
              ? 'Members can now only see experiments they created.'
              : 'Members can now see every experiment in the team.',
        });
      } else {
        const body = await res.json().catch(() => ({}));
        setMode(previousMode);
        addNotification({
          type: 'danger',
          message: `Failed to update setting: ${body.detail || res.statusText}`,
        });
      }
    } catch (e) {
      console.error('Error updating experiment privacy mode:', e);
      setMode(previousMode);
      addNotification({
        type: 'danger',
        message: 'Failed to update experiment visibility setting.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography level="title-lg" mb={1} startDecorator={<LockIcon />}>
          Experiment Visibility
        </Typography>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography level="title-lg" mb={2} startDecorator={<LockIcon />}>
        Experiment Visibility
      </Typography>

      {error && (
        <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box>
            <Typography level="title-md" mb={0.5}>
              Private experiments
            </Typography>
            <Typography level="body-sm" color="neutral">
              When on, members only see and use experiments they created
              themselves — in the web app, the CLI, and the API. Team owners
              still see every experiment. Experiments with no recorded creator
              are treated as owned by the team owner.
            </Typography>
            <Typography level="body-xs" color="neutral" mt={1}>
              You can still share an individual experiment with a member by
              granting them access under Permissions.
            </Typography>
          </Box>
          <Switch
            checked={mode === 'private'}
            onChange={(event) => handleToggle(event.target.checked)}
            disabled={saving}
          />
        </Stack>
      </Card>
    </Box>
  );
}
